import {useEffect, useMemo} from 'react'
import {useParams} from 'react-router-dom'
import {DataErItem} from '../core/_models'
import {CustomRow} from './columns/CustomRow'
import {ErColumns} from './columns/_columns'
import {ErChart} from '../components/ErChart'
import {useAppSelector} from '../../../hooks/redux'
import {csrAPI} from '../../../services/CSRService'
import {KTCardBody} from '../../../template/helpers'
import {useTable, ColumnInstance, Row} from 'react-table'
import {CustomHeaderColumn} from './columns/CustomHeaderColumn'
import {TableLoading} from '../../../template/helpers/TableLoading'
import {formatSeconds} from '../../../template/helpers/formatSeconds'
import {useAccount} from '../../../hooks/useAccount'

const ErTable = () => {
  const {appId} = useParams()
  const query = useAppSelector((state) => state.erFilter)
  const {currentAccountIdForRequest: accountId} = useAccount()
  const [fetch, {data, isLoading}] = csrAPI.useEmployeesFetchReportMutation()

  useEffect(() => {
    if (query && query.period) {
      fetch({id: appId!, filter: {...query}, account_id: accountId})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, accountId])

  const dataItems = data ? (data?.employees as DataErItem[]) : []

  const columns = useMemo(() => ErColumns, [])
  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable({
    columns,
    data: dataItems,
  })

  const total = data?.total
  const totalN = dataItems.reduce((sum, i) => sum + i.total, 0)
  const totalInbound = dataItems.reduce((sum, i) => sum + i.inbound, 0)
  const totalOutbound = dataItems.reduce((sum, i) => sum + i.outbound, 0)
  const totalAvgDuration = dataItems.reduce((sum, i) => sum + i.avg_duration, 0)
  const totalInboundDuration = dataItems.reduce((sum, i) => sum + i.sum_inbound_duration, 0)
  const totalOutboundDuration = dataItems.reduce((sum, i) => sum + i.sum_outbound_duration, 0)
  const totalInbOutbDuration = dataItems.reduce((sum, i) => sum + i.sum_total_duration, 0)

  return (
    <KTCardBody className='pt-1 pb-4'>
      {isLoading && <TableLoading />}
      <ErChart items={dataItems} />
      <div className='table-responsive'>
        <table
          id='kt_table_users'
          className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
          {...getTableProps()}
        >
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              {headers.map((column: ColumnInstance<DataErItem>, i) => (
                <CustomHeaderColumn key={i} column={column} />
              ))}
            </tr>
          </thead>
          <tbody className='text-gray-600 fw-bold' {...getTableBodyProps()}>
            {rows.length > 0 && (
              <tr>
                <td>
                  <div className='d-flex align-content-center small text-warning'>количество сотрудников: {total}</div>
                </td>
                <td>
                  <div className='d-flex align-content-center'>{totalN}</div>
                </td>
                <td>
                  <div className='d-flex align-content-center text-primary'>{totalInbound}</div>
                </td>
                <td>
                  <div className='d-flex align-content-center text-success'>{totalOutbound}</div>
                </td>
                <td>
                  <div className='d-flex align-content-center text-info'>{formatSeconds(totalAvgDuration)}</div>
                </td>
                <td>
                  <div className='d-flex align-content-center text-info'>{formatSeconds(totalInboundDuration)}</div>
                </td>
                <td>
                  <div className='d-flex align-content-center text-info'>{formatSeconds(totalOutboundDuration)}</div>
                </td>
                <td>
                  <div className='d-flex align-content-center text-info'>{formatSeconds(totalInbOutbDuration)}</div>
                </td>
              </tr>
            )}
            {rows.length > 0 ? (
              rows.map((row: Row<DataErItem>, i) => {
                prepareRow(row)
                return <CustomRow row={row} key={`row-${i}-${row.id}`} />
              })
            ) : (
              <tr>
                <td colSpan={7}>
                  <div className='d-flex text-center w-100 align-content-center justify-content-center'>---</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </KTCardBody>
  )
}

export {ErTable}
